/**
 * Form-level errors: the ones that belong to no single field.
 *
 * "The email address or password is incorrect", a rate limit, a server fault.
 * Kept distinct from field errors because the remedy differs — a field error
 * says fix this input, a form error says something else entirely.
 *
 * `tabIndex={-1}` with a ref lets the form move focus here after a failed
 * submit. Without that, a keyboard user submits, nothing appears to happen,
 * and the message they need is somewhere above the viewport.
 */

import { forwardRef } from "react";

import "./forms.css";

export interface FormErrorProps {
  messages: readonly string[];
  title?: string;
}

export const FormError = forwardRef<HTMLDivElement, FormErrorProps>(function FormError(
  { messages, title },
  ref,
) {
  if (messages.length === 0) return null;

  return (
    <div
      ref={ref}
      className="hardauto-form-error"
      role="alert"
      tabIndex={-1}
      data-testid="form-error"
    >
      {title ? <p className="hardauto-form-error__title">{title}</p> : null}
      {messages.length === 1 ? (
        <p className="hardauto-form-error__message">{messages[0]}</p>
      ) : (
        <ul className="hardauto-form-error__list">
          {messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}
    </div>
  );
});
